import React, { useRef, useEffect, useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  RefreshCw,
  Trash2,
} from "lucide-react";
import { streamChatOption, type ChatStreamEvent } from "@/api/chatStreamOption";
import {
  AgentActivityPanel,
  type AgentOperation,
} from "@/components/chat/AgentActivityPanel";
import {
  useGetChatSessionsQuery,
  useLazyGetChatHistoryQuery,
  useDeleteChatSessionMutation,
  useSubmitChatFeedbackMutation,
} from "@/api/chatApi";
import type { RootState } from "@/app/store";
import type { Message } from "@/types";

import {
  addMessage,
  setSessionId,
  setActiveConversation,
  hydrateConversationsFromServer,
  hydrateConversationMessages,
  resetChat,
  removeConversation,
  setLoading,
  setError,
  clearError,
} from "./chatSlice";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { ChatInput, type DatabaseOption } from "@/components/chat/ChatInput";
import { StarterPrompts } from "@/components/chat/StarterPrompts";
import { ChartRenderer } from "@/components/data/ChartRenderer";
import { TableRenderer } from "@/components/data/TableRenderer";
import { KpiCard } from "@/components/data/KpiCard";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { cn, generateId } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageContext";
import { Navbar } from "@/components/layout/Navbar";
import { getAuthUser, isAuthenticated } from "@/lib/auth";
import { buildWelcomeTitle } from "@/lib/welcome";
import type { UserRead } from "@/types/auth";

type ChatViewProps = {
  publicMode?: boolean;
  embedMode?: boolean;
};

export const ChatView: React.FC<ChatViewProps> = ({
  publicMode = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { messages, session_id, isLoading, error, conversations, activeConversationId } = useSelector(
    (state: RootState) => state.chat,
  );
  const authed = useMemo(() => !publicMode && isAuthenticated(), [publicMode]);
  const { data: serverSessions } = useGetChatSessionsQuery(
    authed ? { limit: 100 } : undefined,
    { skip: !authed },
  );
  const [fetchHistory] = useLazyGetChatHistoryQuery();
  const [deleteChatSession] = useDeleteChatSessionMutation();
  const [submitChatFeedback] = useSubmitChatFeedbackMutation();
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const activeRequestRef = useRef<AbortController | null>(null);
  const [chartOverrides, setChartOverrides] = useState<Record<string, string>>({});
  const [streamStageLabel, setStreamStageLabel] = useState<string>(t("loading.title"));
  const [agentThinking, setAgentThinking] = useState<string[]>([]);
  const [agentOperations, setAgentOperations] = useState<AgentOperation[]>([]);
  const [agentActivityVisible, setAgentActivityVisible] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmOverlayPos, setConfirmOverlayPos] = useState<{ top: number; left: number } | null>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const confirmCardRef = useRef<HTMLDivElement | null>(null);
  const historyListRef = useRef<HTMLDivElement | null>(null);
  const [welcomeRefreshKey, setWelcomeRefreshKey] = useState(0);
  const operationSeqRef = useRef(0);
  const user = useMemo(() => getAuthUser<UserRead>(), []);
  const welcomeTitle = useMemo(
    () => buildWelcomeTitle(t, user),
    // welcomeRefreshKey ensures greeting updates when starting a new chat
    [t, user, welcomeRefreshKey],
  );

  const historyItems = useMemo(
    () => conversations
      .filter((conv) => conv.messages.length > 0 || Boolean(conv.session_id))
      .map((conv) => {
        const firstUserMessage = conv.messages.find((m) => m.sender === "user")?.text;
        const title = firstUserMessage
          ? firstUserMessage.slice(0, 48).trim() || conv.title
          : conv.title;
        return {
          id: conv.id,
          title,
          updatedAt: conv.updatedAt,
        };
      })
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
    [conversations],
  );

  const activeConversation = useMemo(
    () => conversations.find((conv) => conv.id === activeConversationId) ?? conversations[0],
    [conversations, activeConversationId],
  );

  const stageToLabel = useCallback(
    (stage: string | null) => {
      if (!stage) return t("loading.title");
      const stageLabels: Record<string, string> = language === "am"
        ? {
            request_received: "ጥያቄዎ ተቀብሏል...",
            session_ready: "የውይይት ክፍለ ጊዜ በማዘጋጀት ላይ...",
            detecting_language: "ቋንቋ በመለየት ላይ...",
            translating_input: "ግብዓቱን በመተርጎም ላይ...",
            language_ready: "ቋንቋ ዝግጁ ነው...",
            classifying_intent: "የጥያቄውን ዓላማ በመረዳት ላይ...",
            needs_clarification: "ከመጠየቅዎ በፊት አንድ ዝርዝር እጠይቃለሁ...",
            handling_meta_or_introduction: "የምላሽ አውድ በማዘጋጀት ላይ...",
            handling_general_knowledge: "ምላሽ በመገንባት ላይ...",
            connecting_database: "ወደ ዳታቤዝ በመገናኘት ላይ...",
            planning_query: "ተመጣጣኝ ጥያቄ በማቀድ ላይ...",
            generating_sql: "SQL በመፍጠር ላይ...",
            executing_sql: "ጥያቄውን በማስኬድ ላይ...",
            synthesizing_response: "ምላሹን በመጻፍ ላይ...",
            translating_output: "ውጤቱን በመተርጎም ላይ...",
            finalizing_response: "ምላሹን በመጨረስ ላይ...",
            completed: "ተጠናቋል።",
          }
        : {
            request_received: "Received your request...",
            session_ready: "Preparing session context...",
            detecting_language: "Detecting language...",
            translating_input: "Translating your input...",
            language_ready: "Language ready...",
            classifying_intent: "Understanding your intent...",
            needs_clarification: "Asking a quick clarifying question...",
            handling_meta_or_introduction: "Preparing response context...",
            handling_general_knowledge: "Building the response...",
            connecting_database: "Connecting to the database...",
            planning_query: "Planning the best query...",
            generating_sql: "Generating SQL...",
            executing_sql: "Running the query...",
            synthesizing_response: "Writing the response...",
            translating_output: "Translating the output...",
            finalizing_response: "Finalizing answer...",
            completed: "Completed.",
          };
      return (
        stageLabels[stage] ??
        (language === "am"
          ? `በሂደት ላይ: ${stage.replace(/_/g, " ")}...`
          : `Working: ${stage.replace(/_/g, " ")}...`)
      );
    },
    [t, language],
  );

  const handleSend = useCallback(
    async (input: string, selectedFilters: DatabaseOption[] = []) => {
      if (!input.trim() || isLoading) return;
      const filterInstruction = selectedFilters.length
        ? `\n\nPlease apply these database filters:\n${selectedFilters.map((filter) => `- ${filter.type}: ${filter.value}`).join("\n")}`
        : "";
      const requestInput = `${input}${filterInstruction}`;

      const requestSessionId = activeConversation?.session_id ?? session_id;

      dispatch(clearError());
      dispatch(setLoading(true));

      // Add user message
      const userMessage = {
        id: generateId(),
        sender: "user" as const,
        text: input,
        selectedFilters: selectedFilters.length > 0 ? selectedFilters : undefined,
        timestamp: new Date().toISOString(),
      };
      dispatch(addMessage(userMessage));

      try {
        const controller = new AbortController();
        activeRequestRef.current = controller;
        setStreamStageLabel(stageToLabel("request_received"));
        setAgentThinking([]);
        setAgentOperations([]);
        setAgentActivityVisible(true);
        operationSeqRef.current = 0;
        const response = await streamChatOption({
          input: requestInput,
          session_id: requestSessionId,
          publicMode,
          signal: controller.signal,
          onEvent: (event: ChatStreamEvent) => {
            if (event.type === "connected") {
              setStreamStageLabel(stageToLabel("session_ready"));
              return;
            }

            if (event.type === "thinking") {
              const text = String(event.payload?.text ?? "").trim();
              if (!text) return;
              setAgentThinking((prev) => {
                if (prev[prev.length - 1] === text) return prev;
                return [...prev, text].slice(-12);
              });
              return;
            }

            if (event.type === "operation") {
              const opKey = String(event.payload?.op ?? "operation");
              const title = String(event.payload?.title ?? opKey);
              const detail = String(event.payload?.detail ?? "");
              const statusRaw = String(event.payload?.status ?? "done").toLowerCase();
              const status: AgentOperation["status"] =
                statusRaw === "running"
                  ? "running"
                  : statusRaw === "failed"
                    ? "failed"
                    : "done";
              setAgentOperations((prev) => {
                const idx = prev.findIndex((item) => item.op === opKey && item.title === title);
                const next: AgentOperation = {
                  id: idx >= 0 ? prev[idx].id : `op-${++operationSeqRef.current}`,
                  op: opKey,
                  title,
                  detail: detail || undefined,
                  status,
                };
                if (idx >= 0) {
                  const copy = [...prev];
                  copy[idx] = next;
                  return copy;
                }
                return [...prev, next].slice(-16);
              });
              return;
            }

            if (event.type === "stage") {
              const stage = String(event.payload?.stage ?? "");
              if (!stage) return;
              setStreamStageLabel(stageToLabel(stage));
              return;
            }

            if (event.type === "done") {
              setStreamStageLabel(stageToLabel("completed"));
              setAgentActivityVisible(false);
              return;
            }

            if (event.type === "result") {
              setAgentActivityVisible(false);
              return;
            }

            if (event.type === "error") {
              setStreamStageLabel(
                language === "am"
                  ? "በማመንጨት ሂደት ላይ ችግር ተገኝቷል..."
                  : "Encountered an issue while generating...",
              );
            }
          },
        });

        // Update session ID if provided
        if (response.session_id) {
          dispatch(setSessionId(response.session_id));
        }

        dispatch(
          addMessage({
            id: generateId(),
            sender: "ai",
            text: response.explanation,
            data: response,
            timestamp: new Date().toISOString(),
          }),
        );
      } catch (err: unknown) {
        const error = err as {
          name?: string;
          message?: string;
          data?: { explanation?: string; message?: string } | unknown;
        };
        const isAbort =
          error.name === "AbortError" ||
          String(error.message || "").toLowerCase().includes("aborted");
        if (isAbort) {
          return;
        }
        console.error("Chat error:", err);
        // If the error contains a friendly explanation, show it as an AI message
        const errorData =
          typeof error.data === "object" && error.data !== null
            ? (error.data as { explanation?: string; message?: string })
            : undefined;
        const explanation =
          errorData?.explanation || errorData?.message || error.message;
        if (explanation) {
          dispatch(
            addMessage({
              id: generateId(),
              sender: "ai",
              text: explanation,
              timestamp: new Date().toISOString(),
            }),
          );
        } else {
          dispatch(
            setError(
              errorData?.message || "Failed to send message. Please try again.",
            ),
          );
        }
      } finally {
        activeRequestRef.current = null;
        setStreamStageLabel(t("loading.title"));
        setAgentThinking([]);
        setAgentOperations([]);
        setAgentActivityVisible(false);
        dispatch(setLoading(false));
      }
    },
    [
      activeConversation?.session_id,
      session_id,
      isLoading,
      dispatch,
      stageToLabel,
      t,
      language,
      publicMode,
    ],
  );

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length === 0 && !isLoading) {
      return;
    }
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  // Handle example query from support page
  useEffect(() => {
    const state = location.state as { exampleQuery?: string };
    if (state?.exampleQuery && !isLoading) {
      handleSend(state.exampleQuery);
      // Clear the state to prevent repeated execution
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, isLoading, handleSend, navigate, location.pathname]);

  const handleReset = () => {
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
    setStreamStageLabel(t("loading.title"));
    setAgentThinking([]);
    setAgentOperations([]);
    setAgentActivityVisible(false);
    setWelcomeRefreshKey((key) => key + 1);
    dispatch(resetChat());
  };

  const handleSelectConversation = useCallback(
    async (conversationId: string) => {
      dispatch(setActiveConversation(conversationId));
      const conv = conversations.find((c) => c.id === conversationId);
      const serverSessionId = conv?.session_id;
      if (!publicMode && serverSessionId && (!conv?.messages || conv.messages.length === 0)) {
        try {
          const res = await fetchHistory({ session_id: serverSessionId, limit: 200 }).unwrap();
          const mapped: Message[] = res.messages.map((m, idx) => ({
            id: `${serverSessionId}-${idx}`,
            sender: m.role === "user" ? "user" : "ai",
            text: m.message,
            timestamp: new Date().toISOString(),
          }));
          dispatch(hydrateConversationMessages({ session_id: serverSessionId, messages: mapped }));
        } catch {
          // ignore; chat still works with fresh messages
        }
      }
    },
    [dispatch, conversations, fetchHistory, publicMode],
  );

  const handleAssistantFeedback = useCallback(
    async (message: Message, rating: 'helpful' | 'not_helpful') => {
      const feedbackSessionId =
        message.data?.session_id ?? activeConversation?.session_id ?? session_id;
      if (!feedbackSessionId) {
        throw new Error('This response does not have a session to attach feedback to.');
      }
      await submitChatFeedback({
        session_id: feedbackSessionId,
        message_id: message.id,
        response_text: message.text,
        rating,
        publicMode,
      }).unwrap();
    },
    [activeConversation?.session_id, publicMode, session_id, submitChatFeedback],
  );

    const handleDeleteConversation = useCallback(
      async (conversationId: string) => {
        try {
          await deleteChatSession({ session_id: conversationId }).unwrap();
        } catch (err) {
          // ignore API errors and still remove locally
        }
        dispatch(removeConversation(conversationId));
        setConfirmDeleteId(null);
        setConfirmOverlayPos(null);
      },
      [deleteChatSession, dispatch],
    );

  const positionConfirmCard = useCallback((conversationId: string) => {
    const el = itemRefs.current.get(conversationId);
    if (!el) {
      setConfirmOverlayPos(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setConfirmOverlayPos({
      top: Math.round(rect.top + rect.height / 2),
      // The chat history lives on the viewport's left edge, so placing the
      // card to the row's left can put it outside the visible area.
      left: Math.round(rect.right + 8),
    });
  }, []);

  const openDeleteConfirm = useCallback((conversationId: string) => {
    positionConfirmCard(conversationId);
    setConfirmDeleteId(conversationId);
  }, [positionConfirmCard]);

  const closeDeleteConfirm = useCallback(() => {
    setConfirmDeleteId(null);
    setConfirmOverlayPos(null);
  }, []);

  useEffect(() => {
    if (!confirmDeleteId) return;
    const onReposition = () => positionConfirmCard(confirmDeleteId);
    const listEl = historyListRef.current;
    listEl?.addEventListener("scroll", onReposition);
    window.addEventListener("resize", onReposition);
    return () => {
      listEl?.removeEventListener("scroll", onReposition);
      window.removeEventListener("resize", onReposition);
    };
  }, [confirmDeleteId, positionConfirmCard]);

  useEffect(() => {
    if (!confirmDeleteId) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (confirmCardRef.current?.contains(target)) return;
      const itemEl = itemRefs.current.get(confirmDeleteId);
      if (itemEl?.contains(target)) return;
      closeDeleteConfirm();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDeleteConfirm();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [confirmDeleteId, closeDeleteConfirm]);

  useEffect(() => {
    if (sidebarCollapsed) closeDeleteConfirm();
  }, [sidebarCollapsed, closeDeleteConfirm]);

  useEffect(() => {
    if (publicMode) return;
    if (!serverSessions?.sessions?.length) return;
    dispatch(
      hydrateConversationsFromServer(
        serverSessions.sessions.map((s) => ({
          session_id: s.session_id,
          title: s.title,
          updatedAt: s.last_message_at,
        })),
      ),
    );
  }, [dispatch, publicMode, serverSessions]);

  const handleStop = () => {
    activeRequestRef.current?.abort();
    activeRequestRef.current = null;
    setStreamStageLabel(t("loading.title"));
    setAgentThinking([]);
    setAgentOperations([]);
    setAgentActivityVisible(false);
    dispatch(setLoading(false));
  };

  const chartOptions = useMemo(
    () => language === "am"
      ? [
          { type: "bar", label: "ባር" },
          { type: "line", label: "መስመር" },
          { type: "pie", label: "ፓይ" },
          { type: "doughnut", label: "ዶናት" },
        ]
      : [
          { type: "bar", label: "Bar" },
          { type: "line", label: "Line" },
          { type: "pie", label: "Pie" },
          { type: "doughnut", label: "Donut" },
        ],
    [language],
  );

  const setChartType = (messageId: string, chartType: string) => {
    setChartOverrides((prev) => ({ ...prev, [messageId]: chartType }));
  };

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const isWelcomeState = messages.length === 0 && !isLoading;

  return (
    <div className="relative flex h-screen flex-col overflow-hidden" style={{ background: 'radial-gradient(ellipse at 0% 0%, rgba(59,130,246,0.05) 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, rgba(59,130,246,0.04) 0%, transparent 50%), #f8fafc' }}>
      <Navbar publicMode={publicMode} />

      {/* Messages Container */}
      <main
        ref={messagesContainerRef}
        className="relative min-h-0 flex-1 overflow-hidden"
      >
        <div className="flex h-full min-h-0 w-full gap-0 md:gap-4">
            <aside className={`hidden h-full shrink-0 border-r border-primary-100/30 bg-white/90 md:flex md:flex-col shadow-sm ${sidebarCollapsed ? 'w-14 overflow-hidden' : 'w-[260px]'}`}>
            <div className="flex shrink-0 items-center gap-1.5 p-2">
              {!sidebarCollapsed && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary-600 px-2.5 py-2 text-[13px] font-semibold text-white shadow-sm transition hover:bg-primary-700"
                >
                  <RefreshCw className="h-4 w-4 shrink-0" />
                  {t("btn.newChat")}
                </button>
              )}
              <button
                type="button"
                onClick={() => setSidebarCollapsed((prev) => !prev)}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-200/60"
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <span className="flex h-3.5 w-4 flex-col items-center justify-between">
                  <span className="block h-0.5 w-4 rounded-full bg-current" />
                  <span className="block h-0.5 w-4 rounded-full bg-current" />
                  <span className="block h-0.5 w-4 rounded-full bg-current" />
                </span>
              </button>
            </div>
            {!sidebarCollapsed && (
              <div className="relative flex min-h-0 flex-1 flex-col px-2 pb-2">
                <p className="mb-1 px-2 text-[11px] font-medium text-slate-500">
                  {t("chat.sidebar.chats")}
                </p>
                <div ref={historyListRef} className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
              {historyItems.length === 0 ? (
                <p className="px-2 py-2 text-xs text-slate-400">
                  {t("chat.sidebar.empty")}
                </p>
              ) : (
                historyItems.map((item) => {
                  const isActive = item.id === activeConversation?.id;
                  const isConfirming = item.id === confirmDeleteId;
                  return (
                    <div
                      key={item.id}
                      ref={(el) => {
                        if (el) itemRefs.current.set(item.id, el);
                        else itemRefs.current.delete(item.id);
                      }}
                      className={cn(
                        "group flex w-full items-center rounded-lg",
                        isConfirming && "bg-red-50 ring-1 ring-red-200",
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => handleSelectConversation(item.id)}
                        title={item.title}
                        className={cn(
                          "flex-1 min-w-0 items-center rounded-lg px-2.5 py-2 text-left text-[13px] leading-tight transition-colors",
                          "w-full",
                          isConfirming
                            ? "text-slate-900"
                            : isActive
                              ? "bg-slate-200/80 text-slate-900"
                              : "text-slate-600 hover:bg-slate-200/50",
                        )}
                      >
                        <span className="truncate block w-full overflow-hidden">{item.title}</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteConfirm(item.id);
                        }}
                        title="Delete conversation"
                        className={cn(
                          "ml-2 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-opacity duration-150",
                          isConfirming
                            ? "opacity-100 text-red-600"
                            : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto",
                        )}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })
              )}
                </div>
              </div>
            )}
          </aside>

          <section className="min-h-0 flex-1 flex flex-col">
          {isWelcomeState ? (
            <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-8">
              <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[650px] w-[650px] rounded-full bg-primary-200/30 blur-3xl" />
              <div className="relative my-auto flex w-full flex-col items-center">
              <h1
                key={welcomeRefreshKey}
                className="animate-welcome-in text-center text-3xl font-black tracking-tight text-slate-900 opacity-0 md:text-4xl drop-shadow-sm"
              >
                {welcomeTitle}
              </h1>
              <StarterPrompts
                onSelect={handleSend}
                className="mt-7 w-full max-w-4xl"
              />
              <div className="animate-welcome-in-delayed mt-6 w-full max-w-2xl opacity-0">
                <ChatInput
                  variant="floating"
                  onSend={handleSend}
                  loading={isLoading}
                  onStop={handleStop}
                  publicMode={publicMode}
                />
              </div>
              </div>
            </div>
          ) : (
            <>
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
            <div className="mx-auto mb-3 flex max-w-4xl flex-wrap items-center justify-between gap-2 rounded-lg border border-primary-100/70 bg-white/80 px-3 py-2 shadow-sm backdrop-blur">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
                  {t("chat.workspace.title")}
                </p>
                <p className="text-xs text-slate-600">
                  {t("chat.workspace.subtitle")}
                </p>
              </div>
              <div className="rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-semibold text-primary-700">
                {messages.length} message{messages.length === 1 ? "" : "s"}
              </div>
            </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-auto mt-6 max-w-4xl">
            <ErrorBanner message={error} onDismiss={handleDismissError} />
          </div>
        )}

        {/* Messages */}
        <div className="mx-auto mt-4 max-w-4xl space-y-5">
          {messages.map((message) => {
            const data = message.data;
            const chartOverride = chartOverrides[message.id];
            const visualization = data?.visualization;
            const hasKpi = visualization?.chart_type === 'kpi';
            const hasKpiTable = hasKpi && (data?.results?.length ?? 0) > 1;
            const tableAlreadyShownInVisualization = Boolean(
              visualization &&
              !visualization.suggestion_needed &&
              data?.results &&
              data.results.length > 0 &&
              visualization.chart_type !== "kpi"
            );
            const canSwitchChart = Boolean(
              visualization && ((hasKpi && hasKpiTable) || visualization.suggestion_needed)
            );
            const hasVisualizationDetails = Boolean(
              visualization &&
                (
                  visualization.suggestion_needed ||
                  (data?.results?.length ?? 0) > 0 ||
                  (visualization.data?.length ?? 0) > 0
                )
            );
            const hasRawTableDetails = Boolean(
              data?.results && data.results.length > 0 && !tableAlreadyShownInVisualization
            );
            const hasAssistantDetails = hasVisualizationDetails || hasRawTableDetails;

            return (
              <ChatMessage
                key={message.id}
                message={message}
                onSuggestionClick={handleSend}
                onFeedback={message.sender === "ai" ? (rating) => handleAssistantFeedback(message, rating) : undefined}
              >
              {message.sender === "ai" && data && hasAssistantDetails && (
                <div className="mt-4 space-y-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                  {hasVisualizationDetails && visualization && (
                    <div className="mt-4">
                      {canSwitchChart && (
                        <div className="mb-2 flex flex-wrap items-center justify-end gap-1.5 text-[11px]">
                          <span className="rounded-full bg-primary-50 px-2 py-0.5 text-primary-600">
                            {language === "am" ? "የቻርት አይነት" : "Chart type"}
                          </span>
                          {chartOptions.map((option) => {
                            const currentType =
                              chartOverride ?? (hasKpi ? 'bar' : visualization?.chart_type);
                            const isActive = currentType === option.type;
                            return (
                              <button
                                key={option.type}
                                type="button"
                                onClick={() => setChartType(message.id, option.type)}
                                className={cn(
                                  'rounded-full border px-2.5 py-0.5 font-semibold transition-colors',
                                  isActive
                                    ? 'border-primary-300 bg-primary-50 text-primary-700'
                                    : 'border-slate-200 bg-white text-slate-600 hover:border-primary-200 hover:text-primary-700'
                                )}
                              >
                                {option.label}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {visualization.chart_type === 'kpi' && data.results?.length === 1 ? (
                        <KpiCard
                          title={visualization.title}
                          value={data.results[0].value}
                          subtitle={undefined /*
                            data.results[0].region
                              ? `${message.data.results[0].region} • ${message.data.results[0].year ?? ''}`
                              : undefined */}
                        />
                      ) : visualization.chart_type === 'kpi' &&
                        (data.results?.length ?? 0) > 1 ? (
                        <ChartRenderer
                          type={chartOverride ?? 'bar'}
                          title={visualization.title}
                          data={data.results.map((r) => ({
                            label: r.indicator,
                            value: r.value,
                          }))}
                          className="shadow-lg border-0"
                        />
                      ) : visualization.suggestion_needed ? (
                        <ChartRenderer
                          type={chartOverride ?? visualization.chart_type}
                          data={visualization.data}
                          title={visualization.title}
                          className="shadow-lg border-0"
                        />
                      ) : data.results && data.results.length > 0 ? (
                        <TableRenderer
                          title={visualization.title || 'Results'}
                          data={data.results}
                          className="shadow-lg border-0"
                        />
                      ) : null}
                    </div>
                  )}


                  
                  {/* Show table for raw results */}
                  {hasRawTableDetails && data.results && (
                    <details className="group rounded-lg border border-slate-200 bg-white">
                      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-slate-700 transition-colors group-open:border-b group-open:border-slate-200 group-open:bg-slate-50">
                        {language === "am" ? "ጥሬ የውሂብ ሰንጠረዥ አሳይ" : "View raw data table"}
                      </summary>
                      <div className="p-2">
                        <TableRenderer
                          data={data.results}
                          title={t("table.title")}
                          className="shadow-lg border-0"
                        />
                      </div>
                    </details>
                  )}

                  {/* Show SQL query in a code block */}
                  {/* {message.data.sql_query && (
                    <div className="bg-gradient-to-br from-secondary-900 to-secondary-800 text-secondary-100 p-6 rounded-xl shadow-lg border border-secondary-700/50">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-3 h-3 bg-success-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-warning-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-error-400 rounded-full"></div>
                        <span className="text-xs font-medium text-secondary-400 ml-2">
                          Generated SQL Query
                        </span>
                      </div>
                      <pre className="text-sm font-mono overflow-x-auto leading-relaxed">
                        <code className="text-secondary-100">{message.data.sql_query}</code>
                      </pre>
                    </div>
                  )} */}
                </div>
              )}
            </ChatMessage>
            );
          })}
        </div>

        {isLoading && agentActivityVisible && (
          <div className="mx-auto mt-4 flex max-w-2xl justify-center px-1">
            <AgentActivityPanel
              thinkingLines={agentThinking}
              operations={agentOperations}
              currentStageLabel={streamStageLabel}
              language={language}
              isActive
            />
          </div>
        )}

            <div ref={chatBottomRef} />
          </div>
            <div className="relative shrink-0 animate-slide-up">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-white via-white/95 to-transparent"></div>
            <div className="relative border-t border-primary-100/30 bg-white/80 backdrop-blur-sm">
              <ChatInput onSend={handleSend} loading={isLoading} onStop={handleStop} publicMode={publicMode} />
            </div>
          </div>
            </>
          )}
          </section>
        </div>
      </main>
      {confirmDeleteId && confirmOverlayPos && (
        <div
          ref={confirmCardRef}
          role="alertdialog"
          aria-modal="true"
          aria-label={t("chat.sidebar.delete_prompt")}
          style={{
            position: "fixed",
            top: confirmOverlayPos.top,
            left: confirmOverlayPos.left,
            transform: "translateY(-50%)",
            zIndex: 70,
          }}
          className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm shadow-lg"
        >
          <div className="whitespace-nowrap text-sm text-slate-700">
            {t("chat.sidebar.delete_prompt") || "Delete?"}
          </div>
          <button
            type="button"
            onClick={() => { handleDeleteConversation(confirmDeleteId); }}
            className="inline-flex items-center rounded px-3 py-1 text-sm font-semibold text-white bg-red-600 hover:bg-red-700"
          >
            {t("chat.sidebar.delete_confirm") || "Delete"}
          </button>
          <button
            type="button"
            onClick={closeDeleteConfirm}
            className="inline-flex items-center rounded px-3 py-1 text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            {t("chat.sidebar.delete_cancel") || "Cancel"}
          </button>
        </div>
      )}
    </div>
  );
};

